// Client API for the chat feature (shared `chat` DB with the mobile app).
// All helpers take the signed-in user's email; the server lowercases and
// scopes everything by it.

const jsonRequest = async (url, options) => {
  const res = await fetch(url, {
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    ...options,
  });
  const content = await res.json();
  if (!res.ok) throw new Error(content.error || "Request failed");
  return content;
};

// Mirrors the mobile app's DM channel ids: lowercased emails, sorted.
export const dmChannelId = (emailA, emailB) =>
  `dm:${[emailA, emailB]
    .map((e) => (e || "").trim().toLowerCase())
    .sort()
    .join("|||")}`;

// Channel list with last message + unread count per channel.
export const fetchChannels = (email) =>
  jsonRequest(`/api/chat/channels?email=${encodeURIComponent(email)}`);

// Newest messages ascending. `before` pages older history; `after` polls for
// new ones (and returns reactionSync for recent messages' reactions).
export const fetchMessages = ({ channelId, email, before, after }) => {
  const params = new URLSearchParams({ channelId, email });
  if (before) params.set("before", before);
  if (after) params.set("after", after);
  return jsonRequest(`/api/chat/messages?${params.toString()}`);
};

export const sendChatMessage = ({ channelId, email, name, text, attachment }) =>
  jsonRequest("/api/chat/messages", {
    method: "POST",
    body: JSON.stringify({ channelId, email, name, text, attachment }),
  });

// Toggles: same emoji from the same person removes it. Resolves with the
// message's current reactions.
export const toggleReaction = ({ messageId, email, name, emoji }) =>
  jsonRequest("/api/chat/reactions", {
    method: "POST",
    body: JSON.stringify({ messageId, email, name, emoji }),
  });

export const markChannelRead = ({ channelId, email }) =>
  jsonRequest("/api/chat/read", {
    method: "POST",
    body: JSON.stringify({ channelId, email }),
  });

// Everyone you can start a DM with (active users, minus yourself).
export const fetchChatDirectory = (email) =>
  jsonRequest(`/api/chat/directory?email=${encodeURIComponent(email)}`);

// Downscale an image file to a JPEG data URI the same shape the mobile app
// sends ({ type: "image", data }) — keeps chat payloads and the shared DB
// small. Falls back to the original file if the canvas path fails (e.g. HEIC
// the browser can't decode).
export const fileToAttachment = (file, maxDimension = 1280) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read that file."));
    reader.onload = () => {
      const original = reader.result;
      const img = new Image();
      img.onerror = () => resolve({ type: "image", data: original });
      img.onload = () => {
        try {
          const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.round(img.width * scale));
          canvas.height = Math.max(1, Math.round(img.height * scale));
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve({ type: "image", data: canvas.toDataURL("image/jpeg", 0.8) });
        } catch (err) {
          resolve({ type: "image", data: original });
        }
      };
      img.src = original;
    };
    reader.readAsDataURL(file);
  });
